/**
 * game.ts —— 单手牌局状态机与整局管理（PRD 模块 1 / 模块 3、六章流程、AC1.1–AC1.10）
 *
 * 边界：本文件**零 UI 依赖**（不 import React / 不触碰 DOM），可在 Worker 或 Node 里跑。
 * 这是 PRD 8.5「规则引擎自动化接口：无 UI 依赖地驱动一手牌」的落地。
 *
 * 状态机（Phase）：
 *   idle ──startHand()──▶ preflop ──▶ flop ──▶ turn ──▶ river ──▶ showdown ──▶ handComplete
 *                                │        │       │        │
 *                                └────────┴───────┴────────┴──▶ handComplete（仅剩 1 人未弃牌）
 *   handComplete ──startHand()──▶ ...   或 ──▶ gameOver（仅剩 1 名有筹码玩家）
 *
 * 关键口径全部来自 PRD 1.2，实现处均有引用注释。
 */

import { createDeck, type Card } from './cards';
import { evaluateHoleAndBoard } from './evaluator';
import {
  applyAction,
  isBettingRoundComplete,
  legalActions,
  nextToAct,
  resetForNewStreet,
  IllegalActionError,
  type Action,
  type ActionEffect,
  type BettingContext,
  type BettingPlayer,
  type LegalActions,
} from './betting';
import { settlePots, type PotContribution, type Settlement } from './pot';
import { createRngBundle, deriveSeedPair, randomSeed, shuffleInPlace, type RngBundle, type RngState } from './rng';

export const SAVE_SCHEMA_VERSION = 1;

/** 牌局阶段。 */
export type Phase = 'idle' | 'preflop' | 'flop' | 'turn' | 'river' | 'showdown' | 'handComplete' | 'gameOver';

/** 街（与赢率引擎的 Street 对齐）。 */
export type Street = 'preflop' | 'flop' | 'turn' | 'river';

/** 开局设置（PRD 模块 3）。 */
export interface GameConfig {
  /** 桌上总人数（含真人），2–8（Round23 §4.1.5：竖屏 9~10 人全员亮牌摊牌物理不可解，人数上限降至 8）。 */
  readonly seatCount: number;
  /** 每人带入筹码。 */
  readonly startingStack: number;
  readonly smallBlind: number;
  readonly bigBlind: number;
  /** 前注，默认 0（PRD 1.2：P2 开关项，默认关闭）。 */
  readonly ante?: number;
  /**
   * 真人玩家座位号，默认 0。
   * 传 null 表示「全 AI 桌」——用于 CI 大样本模拟与风格标定（PRD 8.5 自动化接口）。
   */
  readonly humanSeat?: number | null;
  /** 随机种子（留空则随机）。 */
  readonly seed?: number;
  /** 各座位显示名，可选。 */
  readonly names?: readonly string[];
}

/** 座位上的玩家。 */
export interface Player extends BettingPlayer {
  readonly name: string;
  readonly isHuman: boolean;
  /** 是否已被淘汰（筹码归零且不在本手中）。 */
  eliminated: boolean;
  /** 本手底牌；未发牌或已被清理时为 null。 */
  hole: readonly [Card, Card] | null;
  /** 本手是否已入座参与（淘汰玩家不参与）。 */
  inHand: boolean;
}

/** 手牌历史事件（供回放与复盘，PRD 5.2 / AC5.4）。 */
export type HandEvent =
  | { readonly kind: 'handStart'; readonly handNumber: number; readonly buttonSeat: number; readonly sbSeat: number; readonly bbSeat: number }
  | { readonly kind: 'ante'; readonly seat: number; readonly amount: number }
  | { readonly kind: 'blind'; readonly seat: number; readonly amount: number; readonly blind: 'sb' | 'bb'; readonly allIn: boolean }
  | { readonly kind: 'deal'; readonly seat: number; readonly cards: readonly [Card, Card] }
  | { readonly kind: 'street'; readonly street: Street; readonly board: readonly Card[] }
  | { readonly kind: 'action'; readonly street: Street; readonly effect: ActionEffect; readonly potBefore: number }
  | { readonly kind: 'showdown'; readonly reveals: readonly { readonly seat: number; readonly hole: readonly [Card, Card]; readonly value: number }[] }
  | { readonly kind: 'settle'; readonly settlement: Settlement }
  | { readonly kind: 'eliminated'; readonly seat: number }
  | { readonly kind: 'gameOver'; readonly winnerSeat: number };

/** 一手牌的完整记录。 */
export interface HandRecord {
  readonly handNumber: number;
  readonly buttonSeat: number;
  readonly events: readonly HandEvent[];
  readonly board: readonly Card[];
  readonly settlement: Settlement | null;
  /** 每座位本手净收益（结算所得 − 投入）。 */
  readonly net: ReadonlyMap<number, number>;
}

/** 存档快照（PRD 5.2 / AC1.9 / AC5.6）。 */
export interface SaveSnapshot {
  readonly schemaVersion: number;
  readonly config: GameConfig;
  readonly phase: Phase;
  readonly handNumber: number;
  readonly buttonSeat: number;
  /**
   * 大盲座位（盲注轮转锚点，AC1.8）。
   * 可选是为了兼容不带该字段的旧存档：缺失时从手牌历史的 handStart 事件回推。
   */
  readonly bbSeat?: number;
  readonly players: readonly {
    readonly seat: number;
    readonly name: string;
    readonly isHuman: boolean;
    readonly stack: number;
    readonly eliminated: boolean;
    readonly folded: boolean;
    readonly allIn: boolean;
    readonly inHand: boolean;
    readonly hole: readonly [Card, Card] | null;
    readonly streetContributed: number;
    readonly handContributed: number;
    readonly hasActedThisStreet: boolean;
    readonly raiseRightRevoked: boolean;
  }[];
  readonly board: readonly Card[];
  readonly deck: readonly Card[];
  readonly deckIndex: number;
  readonly currentBet: number;
  readonly lastRaiseSize: number;
  readonly toAct: number | null;
  /** 用户可见种子。 */
  readonly userSeed: number;
  /** 两条流的种子与已消耗步数（PRD 8.4）。 */
  readonly rng: { readonly shuffle: RngState; readonly ai: RngState };
  /** 手牌历史（上限 500 手，PRD 5.2）。 */
  readonly history: readonly HandRecord[];
}

/** 发牌注入脚本（PRD 8.5 牌序注入）。 */
export interface DealScript {
  /** 按座位号指定底牌；未指定的座位从剩余牌堆发。 */
  readonly hole?: Readonly<Record<number, readonly [Card, Card]>>;
  /** 指定 5 张公共牌（可只给前若干张）。 */
  readonly board?: readonly Card[];
}

const HISTORY_LIMIT = 500;

/** 从手牌历史里回推最近一手的大盲座位（旧存档兼容）。 */
function lastBbSeatOf(history: readonly HandRecord[]): number {
  const last = history[history.length - 1];
  if (!last) return -1;
  const start = last.events.find((e) => e.kind === 'handStart');
  return start && start.kind === 'handStart' ? start.bbSeat : -1;
}

/** 校验开局设置（AC3.1）。 */
export function validateConfig(c: GameConfig): void {
  const errs: string[] = [];
  if (!Number.isInteger(c.seatCount) || c.seatCount < 2 || c.seatCount > 8) {
    errs.push(`桌上总人数必须是 2–8 的整数，收到 ${c.seatCount}`);
  }
  if (!Number.isInteger(c.startingStack) || c.startingStack <= 0) {
    errs.push(`带入筹码必须是正整数，收到 ${c.startingStack}`);
  }
  if (!Number.isInteger(c.smallBlind) || c.smallBlind <= 0) errs.push(`小盲必须是正整数，收到 ${c.smallBlind}`);
  if (!Number.isInteger(c.bigBlind) || c.bigBlind <= 0) errs.push(`大盲必须是正整数，收到 ${c.bigBlind}`);
  if (c.smallBlind > c.bigBlind) errs.push(`小盲 ${c.smallBlind} 不能大于大盲 ${c.bigBlind}`);
  if (c.bigBlind >= c.startingStack) errs.push(`大盲 ${c.bigBlind} 必须小于带入筹码 ${c.startingStack}`);
  const ante = c.ante ?? 0;
  if (!Number.isInteger(ante) || ante < 0) errs.push(`前注必须是非负整数，收到 ${ante}`);
  if (ante + c.bigBlind >= c.startingStack) errs.push(`前注 + 大盲（${ante + c.bigBlind}）必须小于带入筹码 ${c.startingStack}`);
  const human = c.humanSeat === null ? null : c.humanSeat ?? 0;
  if (human !== null && (!Number.isInteger(human) || human < 0 || human >= c.seatCount)) {
    errs.push(`真人座位号必须在 0–${c.seatCount - 1} 范围内（或传 null 表示全 AI 桌），收到 ${human}`);
  }
  if (errs.length) throw new Error(`开局设置非法：\n- ${errs.join('\n- ')}`);
}

/**
 * 牌局引擎。
 *
 * 使用方式（无 UI 驱动，PRD 8.5）：
 *   const g = new Game({ seatCount: 3, startingStack: 1000, smallBlind: 10, bigBlind: 20, seed: 1 });
 *   g.startHand();
 *   while (g.phase !== 'handComplete') g.act(g.toAct!, { type: 'call' });
 */
export class Game {
  readonly config: GameConfig;
  readonly players: (Player | null)[];
  private readonly seatCount: number;
  private readonly ante: number;

  phase: Phase = 'idle';
  handNumber = 0;
  /** 按钮座位。首手前为 −1，startHand 时轮转到 0。 */
  buttonSeat = -1;
  /**
   * 本手（或最近一手）的大盲座位，−1 表示尚未开过手。
   * 这是盲注轮转的**锚点**：每手大盲必须前进到下一位在座玩家（AC1.8）。
   */
  bbSeat = -1;
  board: Card[] = [];
  toAct: number | null = null;

  private deck: Card[] = [];
  private deckIndex = 0;
  private ctx: BettingContext;
  private events: HandEvent[] = [];
  private history: HandRecord[] = [];
  private lastSettlement: Settlement | null = null;
  readonly rng: RngBundle;
  /** 本局用户可见种子（PRD 模块 3：可查看 / 可输入以复现）。 */
  readonly userSeed: number;

  constructor(config: GameConfig, rng?: RngBundle) {
    validateConfig(config);
    this.config = config;
    this.seatCount = config.seatCount;
    this.ante = config.ante ?? 0;
    // 单个用户可见种子派生两条独立流（PRD 模块 3 + 8.4）
    this.userSeed = config.seed ?? randomSeed();
    this.rng = rng ?? createRngBundle(deriveSeedPair(this.userSeed));

    const humanSeat = config.humanSeat === null ? -1 : config.humanSeat ?? 0;
    this.players = Array.from({ length: this.seatCount }, (_, seat) => ({
      seat,
      name: config.names?.[seat] ?? (seat === humanSeat ? '你' : `电脑${seat}`),
      isHuman: seat === humanSeat,
      stack: config.startingStack,
      streetContributed: 0,
      handContributed: 0,
      folded: false,
      allIn: false,
      hasActedThisStreet: false,
      raiseRightRevoked: false,
      eliminated: false,
      hole: null,
      inHand: false,
    }));

    this.ctx = {
      players: this.players,
      currentBet: 0,
      lastRaiseSize: config.bigBlind,
      bigBlind: config.bigBlind,
    };
  }

  /* ---------------- 只读查询 ---------------- */

  /** 当前街。showdown / handComplete 时返回最后一街。 */
  get street(): Street {
    switch (this.phase) {
      case 'preflop': return 'preflop';
      case 'flop': return 'flop';
      case 'turn': return 'turn';
      default: return this.board.length >= 5 ? 'river' : this.board.length >= 4 ? 'turn' : this.board.length >= 3 ? 'flop' : 'preflop';
    }
  }

  /** 当前底池总额（含本街已投入）。 */
  get pot(): number {
    let sum = 0;
    for (const p of this.players) if (p) sum += p.handContributed;
    return sum;
  }

  get currentBet(): number { return this.ctx.currentBet; }
  get lastRaiseSize(): number { return this.ctx.lastRaiseSize; }

  /** 本手未弃牌的玩家座位。 */
  activeSeats(): number[] {
    return this.players.filter((p): p is Player => !!p && p.inHand && !p.folded).map((p) => p.seat);
  }

  /** 仍有筹码（未淘汰）的座位。 */
  fundedSeats(): number[] {
    return this.players.filter((p): p is Player => !!p && !p.eliminated && p.stack > 0).map((p) => p.seat);
  }

  /** 某座位当前的合法动作集合（AC1.2 的 UI 数据源）。 */
  legalActionsFor(seat: number): LegalActions {
    return legalActions(this.ctx, seat);
}

  /** 本手事件流（回放用）。 */
  currentEvents(): readonly HandEvent[] { return this.events; }
  /**
   * 当前手的角色位（庄家 D / 小盲 SB / 大盲 BB），取自本手 `handStart` 事件。
   * 无进行中的手牌（尚未开局）时返回 null。UI 徽标（R11-2）只读此结果，不改引擎口径。
   */
  currentRoles(): { buttonSeat: number; sbSeat: number; bbSeat: number } | null {
    for (let i = this.events.length - 1; i >= 0; i--) {
      const e = this.events[i];
      if (e.kind === 'handStart') return { buttonSeat: e.buttonSeat, sbSeat: e.sbSeat, bbSeat: e.bbSeat };
    }
    return null;
  }
  /** 已完成的手牌历史。 */
  handHistory(): readonly HandRecord[] { return this.history; }

  /* ---------------- 一手牌的生命周期 ---------------- */

  /**
   * 开始新的一手。
   * @param script 可选的牌序注入（PRD 8.5），用于构造确定性用例。
   */
  startHand(script?: DealScript): void {
    if (this.phase !== 'idle' && this.phase !== 'handComplete') {
      throw new Error(`当前阶段 ${this.phase} 不能开始新的一手`);
    }
    if (this.fundedSeats().length < 2) {
      throw new Error('有筹码的玩家不足 2 人，对局已结束');
    }

    this.handNumber += 1;
    this.events = [];
    this.board = [];
    this.lastSettlement = null;
    this.deckIndex = 0;

    // --- 1. 确定本手参与者并重置本手状态（轮转要先知道谁还在座）---
    for (const p of this.players) {
      if (!p) continue;
      if (p.stack <= 0) p.eliminated = true;
      p.inHand = !p.eliminated;
      p.folded = !p.inHand;
      p.allIn = false;
      p.hole = null;
      p.streetContributed = 0;
      p.handContributed = 0;
      p.hasActedThisStreet = false;
      p.raiseRightRevoked = false;
    }

    const seated = this.players.filter((p): p is Player => !!p && p.inHand).map((p) => p.seat);
    const headsUp = seated.length === 2;

    // --- 2. 盲注与按钮轮转：dead button 规则（PRD 1.2 / AC1.8）---
    // 锚点是**大盲位**：每手必须前进到下一位在座玩家，因此同一玩家绝不可能连续两手支付大盲。
    // 小盲位与按钮位由大盲位按座位号反推，允许落在已被淘汰的座位（dead button / dead small blind）。
    // 反过来（旧实现）从按钮位正推盲注，按钮落到死座时大盲会原地不动 → 连续两手大盲。
    let sbSeat: number;
    let bbSeat: number;
    let sbLive: boolean;
    if (this.bbSeat < 0) {
      // 首手（或旧版存档缺少大盲锚点）：按钮从 0 开始，其左侧依次为小盲、大盲
      this.buttonSeat = this.buttonSeat < 0 ? 0 : (this.buttonSeat + 1) % this.seatCount;
      if (headsUp && !this.players[this.buttonSeat]?.inHand) this.buttonSeat = this.nextSeated(this.buttonSeat);
      sbSeat = headsUp ? this.buttonSeat : this.nextSeated(this.buttonSeat);
      bbSeat = this.nextSeated(sbSeat);
      sbLive = true;
    } else if (headsUp) {
      // 2 人单挑：大盲前进到对手，按钮位 = 小盲（PRD 1.2「按钮位为小盲」）
      bbSeat = this.nextSeated(this.bbSeat);
      sbSeat = seated.find((s) => s !== bbSeat) ?? bbSeat;
      this.buttonSeat = sbSeat;
      sbLive = true;
    } else {
      bbSeat = this.nextSeated(this.bbSeat);
      sbSeat = (bbSeat - 1 + this.seatCount) % this.seatCount;
      // 小盲座位已被淘汰 → dead small blind：本手不收小盲，位置照旧空着
      sbLive = !!this.players[sbSeat]?.inHand;
      this.buttonSeat = (sbSeat - 1 + this.seatCount) % this.seatCount;
    }
    this.bbSeat = bbSeat;
    // 发牌起点必须是真实在座的玩家（小盲位可能是死座）
    const dealFrom = sbLive ? sbSeat : this.nextSeated(sbSeat);

    this.events.push({ kind: 'handStart', handNumber: this.handNumber, buttonSeat: this.buttonSeat, sbSeat, bbSeat });

    // --- 4. 洗牌 / 应用注入脚本 ---
    this.deck = createDeck();
    if (!script) shuffleInPlace(this.deck, this.rng.shuffleRNG);

    // --- 5. 先收前注入主池，再收盲注（PRD 1.2：前注不计入已下注额）---
    this.ctx.currentBet = 0;
    this.ctx.lastRaiseSize = this.config.bigBlind;
    if (this.ante > 0) {
      for (const seat of seated) {
        const p = this.players[seat] as Player;
        const amt = Math.min(this.ante, p.stack);
        p.stack -= amt;
        p.handContributed += amt;
        if (p.stack === 0) p.allIn = true;
        this.events.push({ kind: 'ante', seat, amount: amt });
      }
    }
    if (sbLive) this.postBlind(sbSeat, this.config.smallBlind, 'sb');
    this.postBlind(bbSeat, this.config.bigBlind, 'bb');
    // 注额水位由大盲确定；最小加注增量 = 大盲
    this.ctx.currentBet = Math.max(...seated.map((s) => (this.players[s] as Player).streetContributed));
    this.ctx.lastRaiseSize = this.config.bigBlind;

    // --- 6. 发底牌 ---
    // 注入的公共牌必须在发底牌**之前**从牌堆剔除，否则未指定底牌的座位可能抽到
    // 指定的公共牌，导致同一手牌出现重复牌（UI 集成自测时发现，属牌序注入路径的缺陷）。
    if (script?.board?.length) this.removeFromDeck(script.board);
    this.dealHoleCards(seated, dealFrom, script);
    this.scriptedBoard = script?.board ? [...script.board] : null;

    // --- 7. 进入翻牌前下注轮 ---
    this.phase = 'preflop';
    this.events.push({ kind: 'street', street: 'preflop', board: [] });
    // 翻牌前首位行动：单挑为按钮（小盲），否则为大盲左侧（UTG）
    this.toAct = headsUp ? this.buttonSeat : this.nextActionable(bbSeat);
    // 极端情况：盲注就把所有人打成全下，本街无人可行动
    if (isBettingRoundComplete(this.ctx)) {
      this.toAct = null;
      this.advanceStreet();
    }
  }

  private scriptedBoard: Card[] | null = null;

  private postBlind(seat: number, amount: number, blind: 'sb' | 'bb'): void {
    const p = this.players[seat] as Player;
    const amt = Math.min(amount, p.stack);
    p.stack -= amt;
    p.streetContributed += amt;
    p.handContributed += amt;
    if (p.stack === 0) p.allIn = true;
    // 盲注是被动投入，不算「已行动」——大盲仍保有 option（可加注）
    this.events.push({ kind: 'blind', seat, amount: amt, blind, allIn: p.allIn });
  }

  private dealHoleCards(seated: readonly number[], sbSeat: number, script?: DealScript): void {
    // 先把脚本指定的底牌从牌堆剔除，避免重复发牌
    if (script?.hole) {
      for (const cards of Object.values(script.hole)) this.removeFromDeck(cards);
    }
    // 发牌顺序：从小盲位开始顺时针（本引擎一次发 2 张，不影响任何概率口径）
    const start = seated.indexOf(sbSeat);
    for (let i = 0; i < seated.length; i++) {
      const seat = seated[(start + i) % seated.length];
      const p = this.players[seat] as Player;
      const scripted = script?.hole?.[seat];
      p.hole = scripted ? ([scripted[0], scripted[1]] as const) : ([this.draw(), this.draw()] as const);
      this.events.push({ kind: 'deal', seat, cards: p.hole });
    }
  }

  private removeFromDeck(cards: readonly Card[]): void {
    const set = new Set(cards);
    this.deck = this.deck.filter((c, i) => i < this.deckIndex || !set.has(c));
  }

  private draw(): Card {
    if (this.deckIndex >= this.deck.length) throw new Error('牌堆耗尽（AC1.10）');
    return this.deck[this.deckIndex++];
  }

  private nextSeated(from: number): number {
    for (let i = 1; i <= this.seatCount; i++) {
      const seat = (from + i) % this.seatCount;
      if (this.players[seat]?.inHand) return seat;
    }
    throw new Error('找不到下一个参与本手的玩家');
  }

  /** 从 from 之后找第一个可行动（未弃牌、未全下、有筹码）的座位。 */
  private nextActionable(from: number): number | null {
    for (let i = 1; i <= this.seatCount; i++) {
      const seat = (from + i) % this.seatCount;
      const p = this.players[seat];
      if (p && p.inHand && !p.folded && !p.allIn && p.stack > 0) return seat;
    }
    return null;
  }

  /* ---------------- 动作 ---------------- */

  /**
   * 提交一个动作。
   * @throws IllegalActionError 动作非法（AC1.2）；Error 阶段/顺序错误（AC1.10）
   */
  act(seat: number, action: Action): ActionEffect {
    if (this.phase !== 'preflop' && this.phase !== 'flop' && this.phase !== 'turn' && this.phase !== 'river') {
      throw new Error(`当前阶段 ${this.phase} 不接受玩家动作`);
    }
    if (this.toAct === null) throw new Error('当前没有等待行动的玩家');
    if (seat !== this.toAct) {
      // AC1.10：重复 / 乱序动作提交必须被拦截且不破坏状态机
      throw new IllegalActionError(seat, action, 'NOT_YOUR_TURN', `当前应由座位 ${this.toAct} 行动，不是座位 ${seat}`);
    }

    const potBefore = this.pot;
    const effect = applyAction(this.ctx, seat, action);
    this.events.push({ kind: 'action', street: this.street, effect, potBefore });

    // 只剩 1 名未弃牌玩家 → 本手提前结束
    if (this.activeSeats().length === 1) {
      this.toAct = null;
      this.finishHand(true);
      return effect;
    }

    if (isBettingRoundComplete(this.ctx)) {
      this.toAct = null;
      this.advanceStreet();
    } else {
      this.toAct = nextToAct(this.ctx, seat);
      if (this.toAct === null) this.advanceStreet();
    }
    return effect;
  }

  /**
   * 行动超时处理（PRD 模块 3「行动时限」：可过牌则过牌，否则弃牌）。
   */
  actTimeout(seat: number): ActionEffect {
    const legal = this.legalActionsFor(seat);
    return this.act(seat, { type: legal.check ? 'check' : 'fold' });
  }

  /* ---------------- 街推进与结算 ---------------- */

  private advanceStreet(): void {
    // 若已无人需要行动（全下对峙），则一路发到河牌再比牌
    const order: Phase[] = ['preflop', 'flop', 'turn', 'river'];
    const idx = order.indexOf(this.phase);
    if (idx < 0) return;

    if (this.phase === 'river') {
      this.finishHand(false);
      return;
    }

    resetForNewStreet(this.ctx);
    const next = order[idx + 1] as Phase;
    const need = next === 'flop' ? 3 : 1;
    for (let i = 0; i < need; i++) {
      const scripted = this.scriptedBoard?.[this.board.length];
      this.board.push(scripted !== undefined ? scripted : this.draw());
    }
    this.phase = next;
    this.events.push({ kind: 'street', street: next as Street, board: [...this.board] });

    this.toAct = this.nextActionable(this.buttonSeat);
    // 翻牌后首位行动 = 按钮左侧第一个可行动玩家；
    // 单挑时按钮左侧即大盲，因此天然满足 AC1.7「按钮翻牌后后行动」。
    if (this.toAct === null || isBettingRoundComplete(this.ctx)) {
      this.toAct = null;
      this.advanceStreet();
    }
  }

  /**
   * 结束本手：比牌（如需要）→ 结算 → 记录历史 → 淘汰判定。
   * @param uncontested 是否因只剩 1 人未弃牌而提前结束
   */
  private finishHand(uncontested: boolean): void {
    // 补齐未发完的公共牌（全下对峙时仍需发满 5 张才能比牌）
    if (!uncontested) {
      while (this.board.length < 5) {
        const scripted = this.scriptedBoard?.[this.board.length];
        this.board.push(scripted !== undefined ? scripted : this.draw());
      }
    }

    const contributions: PotContribution[] = this.players
      .filter((p): p is Player => !!p && p.handContributed > 0)
      .map((p) => ({ seat: p.seat, contributed: p.handContributed, folded: p.folded }));

    const handValue = new Map<number, number>();
    if (uncontested) {
      const winner = this.activeSeats()[0];
      handValue.set(winner, 0);
    } else {
      this.phase = 'showdown';
      const reveals: { seat: number; hole: readonly [Card, Card]; value: number }[] = [];
      for (const seat of this.activeSeats()) {
        const p = this.players[seat] as Player;
        if (!p.hole) throw new Error(`座位 ${seat} 没有底牌，无法比牌`);
        const value = evaluateHoleAndBoard(p.hole, this.board);
        handValue.set(seat, value);
        reveals.push({ seat, hole: p.hole, value });
      }
      reveals.sort((a, b) => b.value - a.value);
      this.events.push({ kind: 'showdown', reveals });
    }

    const settlement = settlePots(contributions, handValue, this.buttonSeat, this.seatCount);
    this.lastSettlement = settlement;
    for (const [seat, amount] of settlement.payouts) {
      const p = this.players[seat] as Player;
      p.stack += amount;
    }
    this.events.push({ kind: 'settle', settlement });

    // 净收益（结算所得 − 本手投入）
    const net = new Map<number, number>();
    for (const c of contributions) {
      net.set(c.seat, (settlement.payouts.get(c.seat) ?? 0) - c.contributed);
    }

    // 淘汰判定
    for (const p of this.players) {
      if (!p || p.eliminated) continue;
      if (p.stack <= 0) {
        p.eliminated = true;
        this.events.push({ kind: 'eliminated', seat: p.seat });
      }
    }

    this.phase = 'handComplete';
    this.toAct = null;

    const funded = this.fundedSeats();
    if (funded.length === 1) {
      this.events.push({ kind: 'gameOver', winnerSeat: funded[0] });
    }

    this.history.push({
      handNumber: this.handNumber,
      buttonSeat: this.buttonSeat,
      events: [...this.events],
      board: [...this.board],
      settlement,
      net,
    });
    if (this.history.length > HISTORY_LIMIT) this.history.splice(0, this.history.length - HISTORY_LIMIT);

    if (funded.length === 1) this.phase = 'gameOver';
  }

  /** 本手结算结果（handComplete 后可读）。 */
  settlement(): Settlement | null { return this.lastSettlement; }

  /** 全桌筹码总额。用于守恒断言：任何时刻都应等于 seatCount × startingStack。 */
  totalChips(): number {
    let sum = 0;
    for (const p of this.players) if (p) sum += p.stack;
    // 手牌进行中，已投入池的筹码还没派发，需要加回来
    if (this.phase !== 'idle' && this.phase !== 'handComplete' && this.phase !== 'gameOver') sum += this.pot;
    return sum;
  }

  /* ---------------- 存档 ---------------- */

  /** 导出存档快照（PRD 5.2）。 */
  save(): SaveSnapshot {
    return {
      schemaVersion: SAVE_SCHEMA_VERSION,
      config: this.config,
      phase: this.phase,
      handNumber: this.handNumber,
      buttonSeat: this.buttonSeat,
      bbSeat: this.bbSeat,
      players: this.players.filter((p): p is Player => !!p).map((p) => ({
        seat: p.seat, name: p.name, isHuman: p.isHuman, stack: p.stack, eliminated: p.eliminated,
        folded: p.folded, allIn: p.allIn, inHand: p.inHand, hole: p.hole,
        streetContributed: p.streetContributed, handContributed: p.handContributed,
        hasActedThisStreet: p.hasActedThisStreet, raiseRightRevoked: p.raiseRightRevoked,
      })),
      board: [...this.board],
      deck: [...this.deck],
      deckIndex: this.deckIndex,
      currentBet: this.ctx.currentBet,
      lastRaiseSize: this.ctx.lastRaiseSize,
      toAct: this.toAct,
      userSeed: this.userSeed,
      rng: this.rng.snapshot(),
      history: this.history,
    };
  }

  /**
   * 从存档恢复（AC1.9 / AC5.6）。
   * @throws Error schemaVersion 不匹配时抛出明确错误，禁止静默丢档或加载出错乱状态。
   */
  static load(snapshot: SaveSnapshot): Game {
    if (snapshot.schemaVersion !== SAVE_SCHEMA_VERSION) {
      throw new Error(
        `存档版本不兼容：存档为 v${snapshot.schemaVersion}，当前引擎为 v${SAVE_SCHEMA_VERSION}。请放弃该存档并新开对局。`,
      );
    }
    const rng = createRngBundle(deriveSeedPair(snapshot.userSeed));
    rng.restore(snapshot.rng);

    const g = new Game({ ...snapshot.config, seed: snapshot.userSeed }, rng);
    g.phase = snapshot.phase;
    g.handNumber = snapshot.handNumber;
    g.buttonSeat = snapshot.buttonSeat;
    // 大盲锚点：旧存档没有该字段时，从最近一手的 handStart 事件回推，避免轮转从头开始
    g.bbSeat = typeof snapshot.bbSeat === 'number' ? snapshot.bbSeat : lastBbSeatOf(snapshot.history);
    g.board = [...snapshot.board];
    g.deck = [...snapshot.deck];
    g.deckIndex = snapshot.deckIndex;
    g.ctx.currentBet = snapshot.currentBet;
    g.ctx.lastRaiseSize = snapshot.lastRaiseSize;
    g.toAct = snapshot.toAct;
    g.history = [...snapshot.history];

    for (const s of snapshot.players) {
      const p = g.players[s.seat];
      if (!p) continue;
      p.stack = s.stack; p.eliminated = s.eliminated; p.folded = s.folded; p.allIn = s.allIn;
      p.inHand = s.inHand; p.hole = s.hole; p.streetContributed = s.streetContributed;
      p.handContributed = s.handContributed; p.hasActedThisStreet = s.hasActedThisStreet;
      p.raiseRightRevoked = s.raiseRightRevoked;
    }
    return g;
  }
}
